import type { World } from '../sim/world';
import type { SimEvent } from '../sim/events';
import type { Vessel } from '../sim/vessel';
import { AMMO, type AmmoId } from '../data/classes';
import { SECTION_BOUNDS } from '../sim/damage';
import { hostile } from '../sim/captain';
import { MASTS } from '../data/ship';
import type { Sighting } from '../sim/lookout';
import type { Tracker } from './tracking';
import { agoText, bearingText, bearingTo, nmText, relBearing } from './nautical';

// Everything about the fight: the state of our ship (guns, shot, hull
// sections, masts, water in the hold, fire, the people), who's out there,
// a running log of what just happened, and the end of it all.

const AMMO_NAMES: Record<AmmoId, string> = { round: 'Round shot', chain: 'Chain shot', grape: 'Grapeshot', shell: 'Explosive shell' };
const AMMO_HINT: Record<AmmoId, string> = { round: 'hull & guns', chain: 'sails & rigging', grape: 'crews · short range', shell: 'fire · explosive' };
const FACTION_NAMES: Record<string, string> = { navy: 'Navy', pirate: 'Pirate', merchant: 'Merchant', player: 'Ours' };
const MODE_NAMES: Record<string, string> = {
  patrol: 'On passage', investigate: 'Investigating', search: 'Searching', engage: 'Engaging', pursue: 'Giving chase',
  flee: 'Running', regroup: 'Falling back', board: 'Closing to board', ram: 'Bearing down to ram',
};

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, html?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (html !== undefined) e.innerHTML = html;
  return e;
}

function healthColor(f: number): string {
  if (f > 0.75) return 'var(--ok)';
  if (f > 0.4) return '#d6b05a';
  if (f > 0.1) return 'var(--danger)';
  return '#6a2a20';
}

interface ContactRow {
  el: HTMLElement;
  name: HTMLElement;
  dist: HTMLElement;
  sub: HTMLElement;
  state: HTMLElement;
  bar: HTMLElement;
  barFill: HTMLElement;
}

interface LogLine {
  el: HTMLElement;
  t: number;
}

export class CombatHud {
  private readonly ship: HTMLElement;
  private readonly gunsP: HTMLElement;
  private readonly gunsS: HTMLElement;
  private readonly ammo: HTMLElement;
  private readonly hullSvg: SVGSVGElement;
  private readonly sectionEls: SVGPathElement[][] = [];
  private readonly mastEls: Record<string, SVGCircleElement> = {};
  private readonly rudderEl: SVGRectElement;
  private readonly status: HTMLElement;
  private readonly contacts: HTMLElement;
  private readonly log: HTMLElement;
  private readonly prompt: HTMLElement;
  private readonly end: HTMLElement;
  private readonly lines: LogLine[] = [];
  private readonly seen = new Set<number>();
  private readonly identified = new Set<number>();
  private readonly inSight = new Set<number>();
  private readonly contactRows = new Map<number, ContactRow>();
  private readonly contactsHead: HTMLElement;
  private endShown = false;
  private timer = 0;
  private time = 0;

  constructor(hudWrap: HTMLElement, root: HTMLElement, private readonly world: World, private readonly tracker: Tracker, onRestart: () => void) {
    // --- our ship (bottom right)
    this.ship = el('div', 'panel ship-panel');
    this.ship.appendChild(el('div', 'stat-label', 'Broadsides'));
    this.gunsP = el('div', 'gunrow');
    this.gunsS = el('div', 'gunrow');
    this.ship.append(this.gunsP, this.gunsS);
    this.ammo = el('div', 'ammo');
    this.ship.appendChild(this.ammo);
    const cond = el('div', 'condition');
    this.hullSvg = this.buildHull();
    cond.appendChild(this.hullSvg);
    this.status = el('div', 'ship-status');
    cond.appendChild(this.status);
    this.ship.appendChild(cond);
    hudWrap.appendChild(this.ship);
    this.rudderEl = this.hullSvg.querySelector('.rudder-seg') as SVGRectElement;

    // --- contacts (right)
    this.contacts = el('div', 'panel contacts hidden');
    this.contactsHead = el('div', 'stat-label', 'In sight');
    this.contacts.appendChild(this.contactsHead);
    hudWrap.appendChild(this.contacts);

    // --- log (top centre)
    this.log = el('div', 'log');
    hudWrap.appendChild(this.log);

    // --- boarding prompt (bottom centre)
    this.prompt = el('div', 'prompt hidden');
    hudWrap.appendChild(this.prompt);

    // --- end of the voyage
    this.end = el('div', 'endcard hidden');
    this.end.innerHTML = '<div class="end-inner"><div class="end-kicker"></div><h1></h1><p></p><button class="end-go">Put to sea again</button></div>';
    this.end.querySelector('.end-go')!.addEventListener('click', onRestart);
    root.appendChild(this.end);
  }

  private buildHull(): SVGSVGElement {
    // A plan view of the hull, bow to the right: five sections a side.
    const ns = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('viewBox', '-20 -7 42 14');
    svg.classList.add('hull-plan');
    const X0 = -18.1, X1 = 18.9;
    const hb = (x: number) => {
      const u = (x - X0) / (X1 - X0);
      return 5.2 * Math.pow(Math.sin(Math.PI * Math.min(1, u * 0.92 + 0.06)), 0.55) * (u < 0.1 ? 0.75 + u * 2.5 : 1);
    };
    for (let s = 0; s < 5; s++) {
      const a = SECTION_BOUNDS[s]!, b = SECTION_BOUNDS[s + 1]!;
      const row: SVGPathElement[] = [];
      for (const side of [-1, 1]) {
        const pts: string[] = [];
        const n = 6;
        for (let i = 0; i <= n; i++) {
          const x = a + ((b - a) * i) / n;
          pts.push(`${x.toFixed(2)},${(side * hb(x)).toFixed(2)}`);
        }
        pts.push(`${b.toFixed(2)},0`, `${a.toFixed(2)},0`);
        const p = document.createElementNS(ns, 'path');
        // Port is up on the plan (looking down, bow to the right).
        p.setAttribute('d', `M${pts.join(' L')} Z`);
        p.classList.add('sec');
        svg.appendChild(p);
        row.push(p);
      }
      this.sectionEls.push(row);
    }
    for (const m of MASTS) {
      const c = document.createElementNS(ns, 'circle');
      c.setAttribute('cx', String(m.x));
      c.setAttribute('cy', '0');
      c.setAttribute('r', '1.1');
      c.classList.add('mast');
      svg.appendChild(c);
      this.mastEls[m.id] = c;
    }
    const r = document.createElementNS(ns, 'rect');
    r.setAttribute('x', '-19.6');
    r.setAttribute('y', '-0.5');
    r.setAttribute('width', '1.4');
    r.setAttribute('height', '1');
    r.classList.add('rudder-seg');
    svg.appendChild(r);
    return svg;
  }

  /** Messages from sim events. */
  handle(events: SimEvent[]): void {
    const w = this.world;
    const me = w.player;
    const name = (id: number) => w.vessels.find((v) => v.id === id)?.name ?? 'a ship';
    for (const e of events) {
      switch (e.type) {
        case 'mastFall': {
          const v = w.vessels.find((o) => Math.hypot(o.ship.com.x - e.x, o.ship.com.z - e.z) < 30 * o.ship.sx + 10);
          const what = e.level === 3 ? `${e.mast}mast` : e.level === 2 ? `${e.mast} topmast` : `${e.mast} topgallant mast`;
          if (v === me) this.say(`Our ${what} has gone by the board!`, 'bad');
          else if (v) this.say(`${v.name}'s ${what} is down.`, 'good');
          break;
        }
        case 'fire':
          if (e.started && e.vessel === me.id) this.say('Fire! Fire aboard — hands to the buckets!', 'bad', 'fire');
          break;
        case 'explosion':
          if (e.size >= 4 && e.vessel >= 0) this.say(e.vessel === me.id ? 'Our magazine has gone up!' : `${name(e.vessel)} blows up!`, e.vessel === me.id ? 'bad' : 'good');
          break;
        case 'struck':
          this.say(`${name(e.vessel)} strikes her colours.`, 'good');
          break;
        case 'captured':
          if (e.vessel === me.id) this.say('They have carried our deck.', 'bad');
          else this.say(`${name(e.vessel)} is taken by ${name(e.by)}.`, e.by === me.id ? 'good' : '');
          break;
        case 'shipSinking':
          if (e.vessel === me.id) this.say('She is going down! Abandon ship!', 'bad');
          else this.say(`${name(e.vessel)} is sinking.`, 'good');
          break;
        case 'shipSunk':
          if (e.vessel !== me.id) this.say(`${name(e.vessel)} has gone to the bottom.`, '');
          break;
        case 'boarding':
          if (e.a === me.id || e.b === me.id) this.say(`Grapnels across — boarding ${name(e.a === me.id ? e.b : e.a)}!`, '');
          break;
        case 'collision':
          if (Math.hypot(e.x - me.ship.com.x, e.z - me.ship.com.z) < 60) this.say('Collision!', 'bad', 'collision');
          break;
        case 'collapse':
          if (e.what === 'keel' && Math.hypot(e.x - me.ship.com.x, e.z - me.ship.com.z) < 40) this.say('Her back is broken!', 'bad');
          break;
        default:
          break;
      }
    }
  }

  private say(text: string, kind: '' | 'good' | 'bad', dedupe?: string): void {
    if (dedupe && this.lines.some((l) => l.el.dataset.key === dedupe && this.time - l.t < 8)) return;
    const d = el('div', `log-line ${kind}`, text);
    if (dedupe) d.dataset.key = dedupe;
    this.log.prepend(d);
    this.lines.unshift({ el: d, t: this.time });
    while (this.lines.length > 6) this.lines.pop()!.el.remove();
  }

  update(dt: number): void {
    this.time += dt;
    for (const l of this.lines) {
      const age = this.time - l.t;
      l.el.style.opacity = String(Math.max(0, Math.min(1, (10 - age) / 2)));
    }
    while (this.lines.length && this.time - this.lines[this.lines.length - 1]!.t > 10) this.lines.pop()!.el.remove();
    const w = this.world;
    const me = w.player;
    this.renderGuns(this.gunsP, -1, 'Port', 'Q');
    this.renderGuns(this.gunsS, 1, 'Starboard', 'E');
    this.timer -= dt;
    if (this.timer > 0) return;
    this.timer = 0.2;

    // Shot.
    const cur = me.battery.ammo;
    const loaded = new Map<AmmoId, number>();
    for (const g of me.battery.guns) if (g.loaded && !g.destroyed) loaded.set(g.charge, (loaded.get(g.charge) ?? 0) + 1);
    this.ammo.innerHTML = `<span class="gun-key">G</span><span class="ammo-name">${AMMO_NAMES[cur]}</span><span class="ammo-hint">${AMMO_HINT[cur]}</span>`
      + `<div class="ammo-list">${me.cls.ammo.map((a) => `<i class="${a === cur ? 'on' : ''}" title="${AMMO_NAMES[a]}">${a[0]!.toUpperCase()}</i>`).join('')}`
      + `<span class="ammo-loaded">${[...loaded.entries()].map(([a, n]) => `${n} ${AMMO[a].id}`).join(' · ') || 'none loaded'}</span></div>`;

    // Hull plan.
    const d = me.damage;
    this.sectionEls.forEach((row, s) => {
      row.forEach((p, i) => {
        const f = Math.max(0, d.hull[s]![i === 0 ? 0 : 1]! / d.hullMax);
        p.style.fill = healthColor(f);
        p.style.fillOpacity = String(0.35 + 0.5 * (1 - f));
      });
    });
    for (const m of MASTS) {
      const c = this.mastEls[m.id]!;
      const md = d.masts[m.id];
      c.style.display = md ? '' : 'none';
      if (!md) continue;
      c.style.fill = md.lostLevel >= 3 ? 'transparent' : healthColor(md.hp / md.max);
      c.style.stroke = md.lostLevel > 0 ? 'var(--danger)' : 'var(--ink)';
      c.setAttribute('stroke-dasharray', md.lostLevel > 0 ? '0.6 0.4' : '');
    }
    this.rudderEl.style.fill = healthColor(d.rudder);

    // Condition in words.
    const s = me.ship;
    const crew = me.crew;
    const fit = crew.effective;
    const water = s.flooding;
    const leak = d.leakArea;
    const fires = me.fire.burning;
    const rows: string[] = [];
    rows.push(`<div><span>Hands</span><b>${fit}</b><em>/ ${crew.complement}</em><div class="bar"><i style="width:${Math.round(crew.morale * 100)}%;background:${healthColor(crew.morale)}"></i></div></div>`);
    rows.push(`<div><span>Hold</span><b class="${water > 0.3 ? 'warn' : ''}">${Math.round(water * 100)}%</b><em>${leak > 0.01 ? `leaking${crew.pumpers > 0.5 ? ' · pumps manned' : ''}` : crew.pumpers > 0.5 ? 'pumping' : 'dry'}</em></div>`);
    if (fires > 0) rows.push(`<div><span>Fire</span><b class="warn">${fires}</b><em>${crew.fighters > 0 ? 'fighting it' : 'unchecked!'}</em></div>`);
    if (d.holes.length) rows.push(`<div><span>Holes</span><b>${d.holes.filter((h) => h.patched < 0.95).length}</b><em>${d.holes.filter((h) => h.patched >= 0.95).length} plugged</em></div>`);
    if (me.battle) rows.push(`<div class="quarters">Beat to quarters</div>`);
    this.status.innerHTML = rows.join('');

    this.renderContacts();
    this.renderPrompt();
    this.renderEnd();
  }

  private renderGuns(row: HTMLElement, side: 1 | -1, name: string, key: string): void {
    const guns = this.world.player.battery.guns.filter((g) => g.spec.side === side);
    const pips = guns.map((g) => {
      if (g.destroyed) return '<i class="gone"></i>';
      const cls = g.loaded ? (g.crew > 0.2 ? 'ready' : 'loaded') : g.crew > 0.2 ? '' : 'idle';
      return `<i class="${cls}" style="--p:${g.loaded ? 1 : g.reload}"></i>`;
    }).join('');
    const html = `<span class="gun-key">${key}</span><span class="gun-name">${name}</span><span class="pips">${pips}</span>`;
    if (row.innerHTML !== html) row.innerHTML = html;
  }

  private renderContacts(): void {
    const w = this.world;
    const me = w.player;
    const p = me.ship.com;
    // Only what our own lookouts have actually seen.
    const known = w.lookout.known();
    const items: { s: Sighting; v: Vessel | undefined; d: number; brg: number }[] = [];
    for (const s of known) {
      const v = w.vessels.find((o) => o.id === s.vessel);
      const d = Math.hypot(s.x - p.x, s.z - p.z);
      const brg = bearingTo(p.x, p.z, s.x, s.z);
      items.push({ s, v, d, brg });
      if (!this.seen.has(s.vessel)) {
        this.seen.add(s.vessel);
        const what = s.identified && v ? `A ${v.cls.name.toLowerCase()}` : 'A sail';
        this.say(`Sail ho! ${what} ${relBearing(brg - me.ship.heading)}, bearing ${bearingText(brg)}.`, '');
      }
      if (s.identified && v && !this.identified.has(v.id)) {
        this.identified.add(v.id);
        if (hostile(v, me) || hostile(me, v)) this.say(`She's ${v.name} — ${FACTION_NAMES[v.faction]}${v.faction === 'navy' ? ', and she means us harm' : ''}.`, v.faction === 'merchant' ? '' : 'bad');
      }
      const was = this.inSight.has(s.vessel);
      if (s.visible && !was) this.inSight.add(s.vessel);
      if (!s.visible && was) {
        this.inSight.delete(s.vessel);
        this.say(`Lost sight of ${s.identified && v ? v.name : 'the strange sail'}, last seen ${bearingText(brg)}.`, '', `lost${s.vessel}`);
      }
    }
    // In sight first, nearest first.
    items.sort((a, b) => Number(b.s.visible) - Number(a.s.visible) || a.d - b.d);
    const shown = items.slice(0, 6);
    this.contacts.classList.toggle('hidden', shown.length === 0);
    const keep = new Set(shown.map((x) => x.s.vessel));
    for (const [id, row] of this.contactRows) {
      if (keep.has(id)) continue;
      row.el.remove();
      this.contactRows.delete(id);
    }
    let prev: HTMLElement = this.contactsHead;
    for (const { s, v, d, brg } of shown) {
      let row = this.contactRows.get(s.vessel);
      if (!row) {
        row = this.makeRow(s.vessel);
        this.contactRows.set(s.vessel, row);
      }
      if (prev.nextSibling !== row.el) prev.after(row.el);
      prev = row.el;
      const known = s.identified && v;
      row.el.className = `contact ${known ? v.faction : 'unknown'}${s.visible ? '' : ' lost'}${this.tracker.selected === s.vessel ? ' tracked' : ''}`;
      row.name.textContent = known ? v.name : 'Strange sail';
      row.dist.textContent = `${s.visible ? '' : '≈ '}${nmText(d)}`;
      row.sub.textContent = `${known ? `${v.cls.name} · ` : ''}${bearingText(brg)}, ${relBearing(brg - me.ship.heading)}`;
      let state = '';
      if (!s.visible) state = `Lost to sight ${agoText(w.time - s.seen)}`;
      else if (known && d < 4000) {
        state = v.state === 'struck' ? 'Colours struck' : v.state === 'sinking' ? 'Sinking' : v.state === 'captured' ? 'Taken' : MODE_NAMES[v.captain?.mode ?? 'patrol'] ?? '';
      }
      row.state.textContent = state;
      // Her damage can only be made out close to.
      const hull = known && s.visible && d < 2000 ? v.damage.integrity : -1;
      row.bar.style.display = hull < 0 ? 'none' : '';
      if (hull >= 0) {
        row.barFill.style.width = `${Math.round(hull * 100)}%`;
        row.barFill.style.background = healthColor(hull);
      }
    }
  }

  private makeRow(id: number): ContactRow {
    const el = document.createElement('div');
    el.title = 'Keep a glass on her';
    const top = document.createElement('div');
    top.className = 'c-top';
    const flag = document.createElement('i');
    flag.className = 'flag';
    const name = document.createElement('b');
    const dist = document.createElement('span');
    top.append(flag, name, dist);
    const sub = document.createElement('div');
    sub.className = 'c-sub';
    const state = document.createElement('div');
    state.className = 'c-sub';
    const bar = document.createElement('div');
    bar.className = 'bar';
    const barFill = document.createElement('i');
    bar.appendChild(barFill);
    el.append(top, sub, state, bar);
    el.addEventListener('click', () => this.tracker.toggle(id));
    return { el, name, dist, sub, state, bar, barFill };
  }

  private renderPrompt(): void {
    const w = this.world;
    const me = w.player;
    let show = '';
    if (me.boardingWith >= 0) {
      const b = w.vessels.find((v) => v.id === me.boardingWith);
      if (b) show = `Boarding <b>${b.name}</b> — our ${me.crew.fighters} against their ${b.crew.fighters}`;
    } else if (me.fighting) {
      for (const v of w.vessels) {
        if (v === me || !v.alive || !(hostile(me, v) || v.state === 'struck')) continue;
        const d = Math.hypot(v.ship.com.x - me.ship.com.x, v.ship.com.z - me.ship.com.z);
        const beams = 5.5 * (me.ship.sz + v.ship.sz);
        if (d < beams + 18) {
          const rel = Math.hypot(me.ship.vel.x - v.ship.vel.x, me.ship.vel.z - v.ship.vel.z);
          show = rel < 3 ? `<span class="gun-key">B</span> Board ${v.name}` : `Alongside ${v.name} — match her speed to board`;
          break;
        }
      }
    }
    this.prompt.classList.toggle('hidden', !show);
    if (show && this.prompt.innerHTML !== show) this.prompt.innerHTML = show;
  }

  private renderEnd(): void {
    const me = this.world.player;
    if (this.endShown) return;
    let kicker = '', title = '', text = '';
    if (me.state === 'sunk' || (me.state === 'sinking' && me.sinkTime > 25)) {
      kicker = 'Lost with all hands';
      title = `${me.name} has foundered`;
      text = `${me.crew.killed} of her people killed. The sea keeps the rest of the story.`;
    } else if (me.state === 'captured') {
      kicker = 'Taken';
      title = `${me.name} has been carried by boarders`;
      text = 'Her colours come down, and her company go below in irons.';
    }
    if (!title) return;
    this.endShown = true;
    this.end.querySelector('.end-kicker')!.textContent = kicker;
    this.end.querySelector('h1')!.textContent = title;
    this.end.querySelector('p')!.textContent = text;
    setTimeout(() => this.end.classList.remove('hidden'), 3500);
  }
}
